import { isEmpty } from "es-toolkit/compat";
import { type Setter, atom } from "jotai";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import type { DocsListLocalEntry } from "../document-list/document-list.types";
import { fs } from "../filesystem/filesystem.atom";
import { getDocumentFilePath } from "../paths/paths.internal";
import { $metadataCollabConnection } from "../reboot/metadata-collab-connection.atom";
import type { DocumentRefProps } from "../reboot/refs";
import {
	getLatestMetadata,
	makeMetadataRemoteChangesCommand,
	makeMetadataSendToCollabCommand,
} from "../reboot/utils";
import { MetadataLoadError } from "./errors";
import { $loadDocumentMetadata, $setMetadataStateError } from "./metadata-state.atom";

const logger = new Logger("load-metadata-from-reboot");

export interface LoadDocumentMetadataFromRebootInput extends DocumentRefProps {
	key: string;
	localEntry?: DocsListLocalEntry;
}

/**
 * Loads document metadata from the Reboot collab server.
 */
const fsApi = fs();

export const $loadDocumentMetadataFromReboot = atom(
	null,
	async (get, set, input: LoadDocumentMetadataFromRebootInput) => {
		const { key, localEntry, ...props } = input;
		const { documentId } = props;

		try {
			// Get the collab connection
			const connection = await get($metadataCollabConnection(props));
			if (!connection) {
				throw new Error("Reboot connection not available");
			}

			const { rebootStoreRef, store, context } = connection;

			// Read the initial metadata directly from the store's state.
			// The store is already populated with data from queryEvaluatorStateFromReboot.
			// We avoid using store.from("metadata").subscribe() because subscribeQuery
			// silently drops empty results, which would cause the Promise to hang forever
			// if the table has no data.
			const initialMetadata = getLatestMetadata(store) ?? null;

			// Create the collab callbacks from the store
			const sendToCollab = makeMetadataSendToCollabCommand(store);

			// Use the store's current epoch as the initial version for the remoteChanges stream.
			// The remoteChanges command subtracts 1 internally, so we pass the epoch directly.
			// Worst case scenario, we use fromVersion = 0. In this case the Reboot server will
			// send us all the updates it knows about, which may be quite a bit of data, which
			// should be fine.
			const initialVersion = store.latestEpoch;
			const remoteChanges = makeMetadataRemoteChangesCommand(
				rebootStoreRef,
				context,
				initialVersion,
				store
			);

			// Create disk persistence callback if we have a local entry
			let onPersist:
				| ((injectedSet: Setter, metadata: DocumentMetadata) => Promise<void>)
				| undefined = undefined;
			if (!isEmpty(localEntry) && localEntry.path) {
				const metadataPath = await getDocumentFilePath(localEntry.path);
				onPersist = async (
					injectedSet: Setter,
					metadata: DocumentMetadata
				): Promise<void> => {
					////////////////////////////////////////////////////////////////////////////////
					// IMPORTANT: VERY SENSITIVE ZONE. READ BEFORE CHANGING.
					//
					// USE THE `injectedGet` AND `injectedSet` HERE. DO NOT USE `get` AND `set`.
					// `onPersist` is called from atom effects, which do work to make sure writes to
					// atoms we are reading from do not cause infinite loops. You MUST use those
					// injected getters and setters, or in collab mode, this function will persist
					// in a loop forever.
					await injectedSet(fsApi.writeYamlFile, {
						filePath: metadataPath,
						content: metadata,
						documentId,
					});
					// IMPORTANT: END SYNCHRONOUS ZONE. ////////////////////////////////////////////
					////////////////////////////////////////////////////////////////////////////////
				};
			}

			// Load the metadata state. NEVER SSR, obviously, as this is from Reboot.
			set($loadDocumentMetadata, {
				key,
				metadata: initialMetadata,
				ssr: false,
				sendToCollab,
				remoteChanges,
				onPersist,
			});
		} catch (err) {
			const error = new MetadataLoadError(err);
			logger.error("Failed to load metadata from Reboot", { key, err });
			set($setMetadataStateError, { key, error });
		}
	}
);
$loadDocumentMetadataFromReboot.debugLabel = "loadDocumentMetadataFromReboot";
